import { ref } from 'vue'
import { defineStore } from 'pinia'
import { FavSymbolsV2Service } from '@/services/fav-symbols-v2-service'
import { FALLBACK_API_ERROR } from '@/utils/consts'
import axios from 'axios'
import type { FavSymbolResponse } from '@/services/fav-symbols-v2-service/types'
import type { ApiError, MarketType } from '@/utils/types'

type ActionType = 'fetch' | 'push' | 'deleting'

export const useFavSymbolsStore = defineStore('fav-symbols', () => {
  const loading = ref<Record<ActionType, boolean>>({ fetch: true, push: false, deleting: false })
  const error = ref<Record<ActionType, ApiError | null>>({
    fetch: null,
    push: null,
    deleting: null,
  })
  const symbols = ref<FavSymbolResponse[]>([])

  const fetchSymbols = async () => {
    try {
      const req = await FavSymbolsV2Service.fetchFavSymbols()
      symbols.value = req.data
      return req
    } catch (thrown) {
      if (axios.isAxiosError(thrown) && thrown.response) {
        error.value.fetch = {
          message: thrown.response?.data,
          code: thrown.response?.status,
        }
      } else {
        error.value.fetch = FALLBACK_API_ERROR
      }
    } finally {
      loading.value.fetch = false
    }
  }

  const deleteSymbol = async (symbol_id: number) => {
    loading.value.deleting = true
    try {
      await FavSymbolsV2Service.deleteFavSymbol(symbol_id)
      return true
    } catch (thrown) {
      if (axios.isAxiosError(thrown) && thrown.response) {
        error.value.deleting = {
          message: thrown.response?.data,
          code: thrown.response?.status,
        }
      } else {
        error.value.deleting = FALLBACK_API_ERROR
      }
      return false
    } finally {
      loading.value.deleting = false
    }
  }

  const pushSymbol = async (
    exBuy: string,
    exSell: string,
    marketType: MarketType,
    asset: string,
  ) => {
    try {
      await FavSymbolsV2Service.putFavSymbol(exBuy, exSell, marketType, asset)
    } catch (thrown) {
      if (axios.isAxiosError(thrown) && thrown.response) {
        error.value.push = {
          message: thrown.response?.data,
          code: thrown.response?.status,
        }
      } else {
        error.value.push = FALLBACK_API_ERROR
      }
    } finally {
      loading.value.push = false
    }
  }

  return { deleteSymbol, pushSymbol, fetchSymbols, loading, error, symbols }
})
